import {useEffect, useRef} from 'react';
import {useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import {useForecastFetchQuery} from '../../../forecast-app/shared/hooks/useForecastFetchQuery';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import {openBuilderShareDialog} from './ShareReportAction';
import type {
	Person,
	SharePermissionType,
} from '../../../forecast-app/reports-tab/reports-page/actions/share-report/ShareReportModalTypes';
import type {useBuilderShareDialogQuery$data} from './__generated__/useBuilderShareDialogQuery.graphql';

const query = graphql`
	query useBuilderShareDialogQuery($id: String) {
		viewer {
			company {
				savedReport(id: $id) {
					id
					privateAccess
					person {
						id
						fullName
						initials
						email
						active
						profilePictureId
					}
					shares(first: 1000) {
						edges {
							node {
								id
								permissionType
								user {
									id
									email
									isInternal
								}
							}
						}
					}
				}
				allPersons(first: 10000) {
					edges {
						node {
							id
							fullName
							initials
							email
							active
							profilePictureId
						}
					}
				}
			}
		}
	}
`;

/** The schema carries `permissionType` as a plain `String`; narrow it to the two roles the dialog
 * understands rather than casting, so an unrecognised value falls back to the safe default instead
 * of reaching the dialog as a value it doesn't expect. */
const isSharePermissionType = (value: string | null | undefined): value is SharePermissionType =>
	value === 'VIEWER' || value === 'EDITOR';

/** Loads a saved report's owner and shares on demand and opens the share dialog with them. The
 * builder holds no Relay query of its own, and page load must not pay for data only the dialog
 * needs, so the fetch is imperative and happens on the click.
 *
 * `onPrivateAccessChange`, when given, is forwarded to the dialog so the builder can keep its own
 * saved-report state current when the dialog's company-share toggle commits a new value directly to
 * the backend. Taken as a parameter (rather than read from `useReportBuilder` here) so this hook stays
 * usable, and testable, outside the builder's context provider. */
export const useBuilderShareDialog = (
	onPrivateAccessChange?: (privateAccess: boolean) => void
): {open: (savedReportId: string) => void; loading: boolean} => {
	const {formatMessage} = useIntl();
	const {fetch, data, loading} = useForecastFetchQuery<useBuilderShareDialogQuery$data, {id: string}>(query, true);
	// The hook's onSuccess callback takes no arguments, so the resolved report arrives through `data`
	// on a later render. This marks that a click is waiting for it, so stale `data` from a previous
	// open cannot re-trigger the dialog on an unrelated re-render.
	const pendingRef = useRef(false);

	const warnLoadFailed = (): void => {
		createToast({
			duration: 5000,
			warning: true,
			message: formatMessage({id: 'ai_report_builder.share_load_failed'}),
		});
	};

	useEffect(() => {
		if (!pendingRef.current || data === null) {
			return;
		}
		pendingRef.current = false;

		const report = data?.viewer?.company?.savedReport;
		// The dialog renders the owner's name/email/avatar unconditionally, so a report with no
		// owner (the schema allows `person` to be null) must be treated the same as a report that
		// failed to load, rather than reaching the dialog and crashing there.
		if (!report || !report.person) {
			warnLoadFailed();
			return;
		}

		// Matched by email (case-insensitive) so a share row can show the same avatar/name/email
		// block as the owner row. A share whose email has no matching person - an external email
		// with no Forecast account - simply gets no match and keeps the plain-email row.
		const personsByEmail = new Map<string, Person>();
		for (const edge of data?.viewer?.company?.allPersons?.edges ?? []) {
			const node = edge?.node;
			if (!node?.email) {
				continue;
			}
			personsByEmail.set(node.email.toLowerCase(), {
				id: node.id,
				fullName: node.fullName ?? '',
				initials: node.initials ?? '',
				email: node.email,
				active: node.active ?? true,
				profilePictureId: node.profilePictureId ?? '',
			});
		}

		openBuilderShareDialog({
			reportId: report.id,
			privateAccess: report.privateAccess ?? false,
			reportOwner: {
				id: report.person.id,
				fullName: report.person.fullName ?? '',
				initials: report.person.initials ?? '',
				email: report.person.email ?? '',
				active: report.person.active ?? true,
				profilePictureId: report.person.profilePictureId ?? '',
			},
			shares: (report.shares?.edges ?? []).flatMap(edge => {
				if (!edge?.node) {
					return [];
				}
				const email = edge.node.user?.email ?? '';
				return [
					{
						email,
						sharedId: edge.node.id,
						userId: edge.node.user?.id,
						permissionType: isSharePermissionType(edge.node.permissionType) ? edge.node.permissionType : 'VIEWER',
						isInternal: edge.node.user?.isInternal ?? false,
						person: personsByEmail.get(email.toLowerCase()),
					},
				];
			}),
			onPrivateAccessChange,
			// Backed by the same map used to match the shares that were already present when the
			// dialog opened, so a share added afterwards resolves against the same company people and
			// renders identically instead of only picking up its person on the next open.
			resolvePerson: email => personsByEmail.get(email.toLowerCase()),
		});
		// Deliberately keyed on `data` alone: `warnLoadFailed` and `onPrivateAccessChange` are both
		// recreated every render (neither is guaranteed memoised by its caller), so including them
		// would run this effect on every render rather than only when a new fetch result lands.
	}, [data]);

	const open = (savedReportId: string): void => {
		pendingRef.current = true;
		fetch({id: savedReportId}, undefined, () => {
			// A failed fetch must say so. Opening the dialog with no shares would read as "shared with
			// nobody", which is a different and wrong statement.
			pendingRef.current = false;
			warnLoadFailed();
		});
	};

	return {open, loading};
};
